/* eslint-disable react/prop-types, react/jsx-handler-names */

import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import unionby from 'lodash.unionby';
import axios from 'axios';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Favorites from '@material-ui/icons/Favorite';
import AsyncSelect from 'react-select/lib/Async';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import AsyncCreatableSelect from 'react-select/lib/AsyncCreatable';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { connect } from 'react-redux';
import debounce from 'debounce-promise';
import deepEqual from 'react-fast-compare';
import { Chip, Tooltip } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import '../ReactSelectMaterial/ReactSelectMaterial.css';
import { apiCatchBlockFunction, isArrayValidAndNotEmpty, isValidFunction } from '../../constants/CommonUtil';
import { getStringFromObject, setStringPropertyToObject } from '../../constants/lodashUtils';
import { NumberOf } from '../../constants/numberUtils';
import { isObjectValidAndNotEmpty } from '../../constants/nullCheckUtils';

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        padding: '3.5px 9px',
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    noOptionsMessage: {
        padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    },
    singleValue: {
        fontSize: 16,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
    },
    placeholder: {
        position: 'absolute',
        left: 6,
        fontSize: '1rem',
    },
    paper: {
        position: 'absolute',
        zIndex: 898989,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
        minWidth: '1200px',
        width: 'auto',
    },
    divider: {
        height: theme.spacing.unit * 2,
    },
    table: {
        minWidth: 1200,
        borderCollapse: 'separate',
    },
    tableWrapper: {
        maxHeight: '500px',
        overflowY: 'auto',
    },
    stickyHeader: {
        position: 'sticky',
    },
});

function NoOptionsMessage(props) {
    return (
        <Typography
            color="textSecondary"
            {...props.innerProps}
            className={props.selectProps.classes.noOptionsMessage}
        >
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }) {
    return <div ref={inputRef} {...props} />;
}

function Option(props) {
    const isFixedOption = getStringFromObject('data.isFixedOption', props);
    const fixedOptionSetFilter = getStringFromObject('data.fixedOptionSetFilter', props);
    const optionStyleConfig = getStringFromObject('data.optionStyleConfig', props);
    const showOptionDetails = getStringFromObject('data.showOptionDetails', props);
    const getOptionWithDetailsGrid = getStringFromObject('data.getOptionWithDetailsGrid', props);
    const tableColumns = getStringFromObject('data.tableColumns', props);

    if (isArrayValidAndNotEmpty(tableColumns)) {
        return (
            <TableRow
                ref={props.innerRef}
                selected={props.isFocused || props.isSelected}
                style={{
                    cursor: 'pointer',
                    backgroundColor: props.isFocused ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                }}
                {...props.innerProps}
            >
                {
                    tableColumns.map((column, index) => {
                        const fieldPath = column.field || column.path;
                        let cellValue = getStringFromObject(`data.${fieldPath}`, props) || '';
                        if (column.isNumeric) {
                            cellValue = NumberOf(cellValue);
                        }
                        if (column.isArray && isArrayValidAndNotEmpty(cellValue)) {
                            cellValue = cellValue.join(', ');
                        }

                        return (
                            <TableCell
                                key={column.id || index}
                                style={column.cellStyle || {}}
                                padding={column.disablePadding ? 'none' : 'default'}
                            >
                                {cellValue}
                            </TableCell>
                        );
                    })
                }
                {
                    isFixedOption && fixedOptionSetFilter(props.data) && (
                        <TableCell padding="none" style={{ width: '40px' }}>
                            <ListItemIcon style={{ marginRight: '3px' }}>
                                <Favorites nativeColor="#ff0000" />
                            </ListItemIcon>
                        </TableCell>
                    )
                }
            </TableRow>
        );
    }

    let additionalStyle = {
        fontWeight: props.isSelected ? 500 : 400,
        whiteSpace: 'normal',
        height: 'auto',
    };
    if (isArrayValidAndNotEmpty(optionStyleConfig)) {
        const matchedStyleConfig = optionStyleConfig.find((anOptionStyle) => {
            const {
                field,
                value,
            } = anOptionStyle;
            return getStringFromObject(`data.${field}`, props) === value;
        });
        if (isObjectValidAndNotEmpty(matchedStyleConfig)) {
            additionalStyle = {
                ...additionalStyle,
                ...matchedStyleConfig.style,
            };
            if (props.isFocused && !props.isDisabled) {
                additionalStyle = {
                    ...additionalStyle,
                    ...matchedStyleConfig.focusedStyle,
                };
            }
        }
    }

    if (isFixedOption && fixedOptionSetFilter(props.data)) {
        return (
            <MenuItem
                buttonRef={props.innerRef}
                selected={props.isFocused}
                component="div"
                style={additionalStyle}
                {...props.innerProps}
            >
                {
                    showOptionDetails && getOptionWithDetailsGrid ? (
                        getOptionWithDetailsGrid(props)
                    ) : (
                        <ListItemText
                            primary={props.label}
                            style={{
                                padding: '0 2px',
                            }}
                        />
                    )
                }
                <ListItemIcon style={{ marginRight: '3px' }}>
                    <Favorites nativeColor="#ff0000" />
                </ListItemIcon>
            </MenuItem>
        );
    }
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component="div"
            style={additionalStyle}
            {...props.innerProps}
        >
            {
                showOptionDetails && getOptionWithDetailsGrid ? (
                    getOptionWithDetailsGrid(props)
                ) : (
                    props.children
                )
            }
        </MenuItem>
    );
}

function ValueContainer(props) {
    return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MenuList(props) {
    const { selectProps } = props;
    const { tableColumns } = selectProps;
    const hasTableColumns = isArrayValidAndNotEmpty(tableColumns);

    if (hasTableColumns) {
        return (
            <TableBody {...props.innerProps}>
                {props.children}
            </TableBody>
        );
    }

    return <div {...props.innerProps}>{props.children}</div>;
}

function Menu(props) {
    const { selectProps } = props;
    const { tableColumns } = selectProps;
    const hasTableColumns = isArrayValidAndNotEmpty(tableColumns);

    if (hasTableColumns) {
        return (
            <Paper
                square
                className={selectProps.classes.paper}
                {...props.innerProps}
            >
                <div className={selectProps.classes.tableWrapper}>
                    <Table className={selectProps.classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                {tableColumns.map((column, index) => (
                                    <TableCell
                                        key={column.id || index}
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            backgroundColor: '#fff',
                                            ...column.headerStyle,
                                        }}
                                        padding={column.disablePadding ? 'none' : 'default'}
                                    >
                                        <Typography variant="body1">
                                            {column.label || column.header || ''}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        {props.children}
                    </Table>
                </div>
            </Paper>
        );
    }

    return (
        <Paper
            square
            style={{
                whiteSpace: 'nowrap',
                display: 'table',
                width: '100%',
                zIndex: 898989,
            }}
            className={props.selectProps.classes.paper}
            {...props.innerProps}
        >
            {props.children}
        </Paper>
    );
}

function MultiValue(props) {
    return (
        <Tooltip title={props.children}>
            <Chip
                tabIndex={-1}
                label={props.children}
                className={classNames(props.selectProps.classes.chip, {
                    [props.selectProps.classes.chipFocused]: props.isFocused,
                })}
                onDelete={props.removeProps.onClick}
                deleteIcon={<CancelIcon {...props.removeProps} />}
            />
        </Tooltip>
    );
}

class ReactSelectMaterialTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            options: props.options || [],
            fixedOptionSet: props.fixedOptionSet || [],
            fixedOptionSetLoaded: false,
        };

        if (isArrayValidAndNotEmpty(props.fixedOptionSet)) {
            this.state = {
                ...this.state,
                fixedOptionSetLoaded: true,
                fixedOptionSet:
                    (props.fixedOptionSet).map(
                        aFixedOption => ({
                            ...aFixedOption,
                            isFixedOption: true,
                            optionStyleConfig: props.optionStyleConfig,
                            fixedOptionSetFilter: this.props.fixedOptionSetFilter,
                            showOptionDetails: this.props.showOptionDetails,
                            getOptionWithDetailsGrid: this.props.getOptionWithDetailsGrid,
                            tableColumns: props.tableColumns,
                        }),
                    ),
            };
        }

        const wait = 300; // milliseconds
        const loadOptions = inputValue => this.loadOptions(inputValue);
        this.debouncedLoadOptions = debounce(loadOptions, wait, {
            leading: true,
        });
    }

    componentDidMount() {
        if (!this.props.autocomplete && this.props.dataSourceApi) {
            this.fetchOptions(this.props.dataSourceApi);
        }

        if (this.props.fixedOptionsSetApi) {
            const {
                fixedOptionsSetApi,
                dispatch,
            } = this.props;
            axios.get(fixedOptionsSetApi)
                .then((response) => {
                    const responseData = isArrayValidAndNotEmpty(response.data) ? response.data : [];

                    this.setState({
                        fixedOptionSetLoaded: true,
                        fixedOptionSet:
                            (responseData).map(
                                aFixedOption => ({
                                    ...aFixedOption,
                                    isFixedOption: true,
                                    optionStyleConfig: this.props.optionStyleConfig,
                                    fixedOptionSetFilter: this.props.fixedOptionSetFilter,
                                    showOptionDetails: this.props.showOptionDetails,
                                    getOptionWithDetailsGrid: this.props.getOptionWithDetailsGrid,
                                    tableColumns: this.props.tableColumns,
                                }),
                            ),
                    });
                })
                .catch((error) => {
                    apiCatchBlockFunction(error, dispatch);
                });
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            !nextProps.autocomplete &&
            nextProps.dataSourceApi && nextProps.dataSourceApi !== this.props.dataSourceApi
        ) {
            this.fetchOptions(nextProps.dataSourceApi);
        }
        if (!deepEqual(nextProps.options, this.props.options)) {
            this.setState({
                options: nextProps.options,
            });
        }
    }

    getNewOptionData = (createValue) => {
        const actualCreateValue = `${createValue} *`;
        const {
            dataSourceConfig,
        } = this.props;
        const newOption = {};
        setStringPropertyToObject(dataSourceConfig.value, newOption, `${actualCreateValue}`);
        setStringPropertyToObject(dataSourceConfig.text, newOption, actualCreateValue);
        return newOption;
    };

    getOptionValue = (option) => {
        if (isValidFunction(this.props.getOptionValue)) {
            return this.props.getOptionValue(option);
        } else if (isObjectValidAndNotEmpty(this.props.dataSourceConfig)) {
            return getStringFromObject(this.props.dataSourceConfig.value, option);
        }
        return option;
    };

    getOptionLabel = (option) => {
        if (isValidFunction(this.props.getOptionLabel)) {
            return this.props.getOptionLabel(option);
        } else if (isObjectValidAndNotEmpty(this.props.dataSourceConfig)) {
            return getStringFromObject(this.props.dataSourceConfig.text, option);
        }
        return option;
    };

    SingleValue = (props) => {
        const {
            textColor,
        } = this.props;
        return (
            <Typography
                className={props.selectProps.classes.singleValue}
                {...props.innerProps}
                style={{ color: textColor }}
            >
                {props.children}
            </Typography>
        );
    };

    Placeholder = (props) => {
        const { selectProps } = this.props;
        const textFieldProps = selectProps && selectProps.textFieldProps;
        const InputProps = textFieldProps && textFieldProps.InputProps;

        return (
            <Typography
                color="textSecondary"
                className={
                    classNames(props.selectProps.classes.placeholder,
                        InputProps && InputProps.classes && InputProps.classes.placeholder)}
                {...props.innerProps}
            >
                {props.children}
            </Typography>
        );
    };
    Control = (params) => {
        const {
            selectProps,
            errorText,
            helperText,
            onBlur,
            autoFocus,
            onClick,
        } = this.props;
        const textFieldProps = selectProps && selectProps.textFieldProps;
        const InputProps = textFieldProps && textFieldProps.InputProps;

        return (
            <TextField
                fullWidth
                variant="outlined"
                onBlur={onBlur}
                InputProps={{
                    inputComponent,
                    classes: {
                        root: InputProps && InputProps.classes && InputProps.classes.placeholder,
                    },
                    inputProps: {
                        className: params.selectProps.classes.input,
                        inputRef: params.innerRef,
                        children: params.children,
                        ...params.innerProps,
                    },
                    ...InputProps,
                }}
                error={!!(errorText)}
                helperText={errorText || helperText}
                autoFocus={autoFocus}
                onClick={onClick}
                {...params.selectProps.textFieldProps}
            />
        );
    };

    mergeApiFieldsIntoOptions = (apiOptions, targetOptions) => {
        const {
            dataSourceConfig,
            apiFieldsToMerge,
        } = this.props;

        if (!isArrayValidAndNotEmpty(apiFieldsToMerge) || !isArrayValidAndNotEmpty(apiOptions)) {
            return targetOptions;
        }

        const valueKey = dataSourceConfig.value;
        const apiOptionsMap = new Map();

        // Create a map of API options by their value key for quick lookup
        apiOptions.forEach((apiOption) => {
            const optionValue = getStringFromObject(valueKey, apiOption);
            if (optionValue) {
                apiOptionsMap.set(optionValue, apiOption);
            }
        });

        return targetOptions.map((option) => {
            const optionValue = getStringFromObject(valueKey, option);
            const matchingApiOption = apiOptionsMap.get(optionValue);

            if (matchingApiOption) {
                const mergedOption = { ...option };
                apiFieldsToMerge.forEach((field) => {
                    const apiFieldValue = getStringFromObject(field, matchingApiOption);
                    if (apiFieldValue !== undefined && apiFieldValue !== null) {
                        setStringPropertyToObject(field, mergedOption, apiFieldValue);
                    }
                });
                return mergedOption;
            }
            return option;
        });
    };

    fetchOptions = (api) => {
        axios.get(api)
            .then((response) => {
                this.setState({
                    options:
                        (response.data || []).map(
                            aRow => ({
                                ...aRow,
                                optionStyleConfig: this.props.optionStyleConfig,
                                showOptionDetails: this.props.showOptionDetails,
                                getOptionWithDetailsGrid: this.props.getOptionWithDetailsGrid,
                                tableColumns: this.props.tableColumns,
                            }),
                        ),
                });
            })
            .catch((error) => {
                apiCatchBlockFunction(error, this.props.dispatch);
            });
    };

    // this method will search over the fixed option set and the set received from the api call
    handleSearchOverOptionSet = (searchedOptions, searchText) => {
        const {
            fixedOptionSet,
        } = this.state;
        const {
            fixedOptionSetFilter,
            dataSourceConfig,
        } = this.props;
        const lowerCasedSearchText = (searchText || '').toLowerCase();

        let finalOptionSet = [...searchedOptions];

        if (isArrayValidAndNotEmpty(fixedOptionSet)) {
            // iterate and search over fixed option set
            let applicableFixedOptions = fixedOptionSet.filter(anOption => fixedOptionSetFilter(anOption));
            if (searchText) {
                applicableFixedOptions =
                    fixedOptionSet.filter(anOption => (
                        (this.getOptionLabel(anOption) || '').toLowerCase().includes(lowerCasedSearchText) &&
                        fixedOptionSetFilter(anOption)
                    ));
            }

            if (isArrayValidAndNotEmpty(applicableFixedOptions)) {
                // remove the applicable fixed options from original option set
                finalOptionSet = unionby(applicableFixedOptions, finalOptionSet, dataSourceConfig.value);
            }
        }

        finalOptionSet = this.mergeApiFieldsIntoOptions(searchedOptions, finalOptionSet);

        // final option set sort by
        if (isArrayValidAndNotEmpty(finalOptionSet) && searchText) {
            finalOptionSet = finalOptionSet.sort((a, b) => {
                const aTextValue = this.getOptionLabel(a);
                const bTextValue = this.getOptionLabel(b);
                if (aTextValue && aTextValue.startsWith(searchText.toLowerCase())) {
                    return -1;
                }
                if (bTextValue && bTextValue.startsWith(searchText.toLowerCase())) {
                    return 1;
                }
                return 0;
            });
        }

        return finalOptionSet;
    };

    loadOptions = searchText =>
        // eslint-disable-next-line no-new
        (
            axios.get(`${this.props.dataSourceApi}${encodeURIComponent(searchText || '')}`)
                .then(response => this.handleSearchOverOptionSet((response.data || []).map(
                    aRow => ({
                        ...aRow,
                        optionStyleConfig: this.props.optionStyleConfig,
                        showOptionDetails: this.props.showOptionDetails,
                        getOptionWithDetailsGrid: this.props.getOptionWithDetailsGrid,
                        tableColumns: this.props.tableColumns,
                    }),
                ), searchText))
                .catch((error) => {
                    apiCatchBlockFunction(error, this.props.dispatch);
                })
        )
    ;

    render() {
        const {
            classes,
            theme,
            value,
            onChange,
            editable,
            label,
            noLabel,
            multiple,
            onBlur,
            autocomplete,
            creatable,
            isDisabled,
            placeholder,
            cacheOptions,
            autocompleteSelectStyle,
            defaultOptions,
            autoFocus,
            inputRef,
            onClick,
            testId,
            rootStyle,
            tableColumns,
        } = this.props;

        const {
            options,
            fixedOptionSetLoaded,
        } = this.state;

        const selectStyles = {
            input: base => ({
                ...base,
                color: theme.palette.text.primary,
                ...autocompleteSelectStyle,
                '& input': {
                    font: 'inherit',
                },
            }),
        };


        const components = {
            Control: this.Control,
            Menu,
            MenuList,
            NoOptionsMessage,
            Option,
            Placeholder: this.Placeholder,
            SingleValue: this.SingleValue,
            ValueContainer,
            MultiValue,
        };

        if (!editable) {
            return (
                <div>
                    <div className="label">
                        {noLabel ? '' : label}
                    </div>
                    <div className="font-size0-9">
                        {
                            this.getOptionLabel(value)
                        }
                    </div>
                </div>
            );
        }
        const commonProps = {
            classes,
            styles: selectStyles,
            options,
            components,
            isDisabled,
            value,
            onBlur,
            onClick,
            id: `${testId}`,
            ref: inputRef,
            cacheOptions,
            key: fixedOptionSetLoaded,
            autoFocus,
            getOptionValue: this.getOptionValue,
            getOptionLabel: this.getOptionLabel,
            onChange,
            placeholder: placeholder || label,
            isMulti: multiple,
            innerProps: {
                onClick,
            },
            textFieldProps: {
                label: noLabel ? '' : label,
                InputLabelProps: {
                    shrink: true,
                },
            },
            tableColumns,
        };
        if (creatable && autocomplete) {
            return (
                <div
                    className={classes.root}
                    style={rootStyle}
                >
                    <AsyncCreatableSelect
                        {...commonProps}
                        id={testId}
                        // cache options is false because it is causing issues when debouncing
                        // like it doesn't load options for any characters before the debounce call
                        cacheOptions={false}
                        defaultOptions={defaultOptions}
                        getNewOptionData={this.getNewOptionData}
                        loadOptions={inputValue => this.debouncedLoadOptions(inputValue)}
                    />
                </div>
            );
        }

        if (autocomplete) {
            return (
                <div
                    className={classes.root}
                    style={rootStyle}
                >
                    <NoSsr>
                        <AsyncSelect
                            {...commonProps}
                            // cache options is false because it is causing issues when debouncing
                            // like it doesn't load options for any characters before the debounce call
                            cacheOptions={false}
                            defaultOptions={defaultOptions}
                            loadOptions={inputValue => this.debouncedLoadOptions(inputValue)}
                        />
                    </NoSsr>
                </div>
            );
        }
        return (
            <div
                className={classes.root}
                style={rootStyle}
            >
                <NoSsr>
                    <Select
                        {...commonProps}
                    />
                </NoSsr>
            </div>
        );
    }
}

ReactSelectMaterialTable.propTypes = {
    classes: PropTypes.object.isRequired,
    testId: PropTypes.string,
    theme: PropTypes.object.isRequired,
    dataSourceConfig: PropTypes.object.isRequired,
    label: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    dispatch: PropTypes.func,
    onBlur: PropTypes.func,
    getOptionLabel: PropTypes.func,
    getOptionValue: PropTypes.func,
    onClick: PropTypes.func,
    fixedOptionSetFilter: PropTypes.func,
    options: PropTypes.array,
    fixedOptionSet: PropTypes.array,
    optionStyleConfig: PropTypes.array,
    dataSourceApi: PropTypes.string,
    errorText: PropTypes.string,
    editable: PropTypes.bool,
    noLabel: PropTypes.bool,
    multiple: PropTypes.bool,
    autocomplete: PropTypes.bool,
    creatable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    cacheOptions: PropTypes.bool,
    defaultOptions: PropTypes.bool,
    autoFocus: PropTypes.bool,
    textColor: PropTypes.string,
    placeholder: PropTypes.string,
    fixedOptionsSetApi: PropTypes.string,
    helperText: PropTypes.string,
    autocompleteSelectStyle: PropTypes.object,
    rootStyle: PropTypes.object,
    showOptionDetails: PropTypes.bool,
    getOptionWithDetailsGrid: PropTypes.element,
    tableColumns: PropTypes.array,
    apiFieldsToMerge: PropTypes.array,
};

ReactSelectMaterialTable.defaultProps = {
    label: 'Search and Select',
    testId: '',
    value: null,
    onChange: () => {},
    onBlur: () => {},
    onClick: () => {},
    // additional filter logic to the fixed option set
    fixedOptionSetFilter: () => true,
    dispatch: () => {},
    getOptionValue: null,
    getOptionLabel: null,
    options: [],
    fixedOptionSet: [],
    optionStyleConfig: [],
    dataSourceApi: '',
    errorText: '',
    placeholder: '',
    editable: true,
    noLabel: false,
    multiple: false,
    autocomplete: false,
    creatable: false,
    isDisabled: false,
    cacheOptions: true,
    defaultOptions: true,
    autoFocus: false,
    textColor: '#000000',
    fixedOptionsSetApi: '',
    helperText: '',
    autocompleteSelectStyle: {},
    rootStyle: {},
    showOptionDetails: false,
    getOptionWithDetailsGrid: null,
    tableColumns: [],
    apiFieldsToMerge: [],
};

export default connect()(withStyles(styles, { withTheme: true })(ReactSelectMaterialTable));
